// Public pages: portal chooser, sign-in per portal (buyer / seller / staff), buyer & seller registration.
// Each portal has its own dedicated page and account type; each role has a separate UI and login.
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, Building2, Check, Factory, FileCheck2, Globe2, Hexagon, Home, Landmark, LockKeyhole, LogOut, Newspaper,
  ShieldCheck, Ship, ShoppingCart, Target, TrendingUp, Users,
} from 'lucide-react';
import { openCookieSettings } from '../../components/CookieConsent';
import { ContactLink, Copyright, LegalLinks } from '../../components/SiteFooter';
import { ErrorNotice, Field, Notice } from '../../components/ui';
import { LEGAL_DOCS, SITE } from '../../lib/site';
import { auth } from '../../lib/trade';
import type { Portal } from '../../types/trade';
import { HERO_PHOTOS, PRODUCT_PHOTOS } from '../trade/photos';
import { RegistrationIdFields, registrationIdentifiers, useSchemes } from '../trade/Identifiers';
import { CountrySelect } from '../trade/shared';
import { homeFor, ROLE_LABEL, useAuth } from './AuthProvider';

interface PortalMeta {
  title: string;
  eyebrow: string;
  headline: ReactNode;
  text: string;
  points: [typeof Ship, string][];
  cls: string;
}

const PORTALS: Record<string, PortalMeta> = {
  buyer: {
    title: 'Buyer sign-in',
    eyebrow: 'BUYER PORTAL',
    cls: 'portal-buyer',
    headline: <>Source battery materials.<br /><span>Compare offers on spec, documents and price.</span></>,
    text: 'Post requirements for spodumene, lithium chemicals, nickel, cobalt, graphite, cathode materials or cells; set receiving ports; and talk to suppliers directly.',
    points: [
      [ShoppingCart, 'Requirements matched to seller listings'],
      [Ship, 'Receiving ports and Incoterms on every enquiry'],
      [ShieldCheck, 'Accounts approved by platform administrators'],
    ],
  },
  seller: {
    title: 'Seller sign-in',
    eyebrow: 'SELLER PORTAL',
    cls: 'portal-seller',
    headline: <>Sell battery materials.<br /><span>Reach buyers who need your grade.</span></>,
    text: 'List what you supply from the shared catalogue with specs, indicative price and documents, set dispatch ports and contact desks, and answer buyer enquiries.',
    points: [
      [Factory, 'Listings tied to a shared product catalogue'],
      [Ship, 'Dispatch ports and Incoterms on every listing'],
      [Users, 'Matched to open buyer requirements'],
    ],
  },
  staff: {
    title: 'Super Admin & Staff sign-in',
    eyebrow: 'PLATFORM GOVERNANCE & SUPER ADMIN',
    cls: 'portal-staff',
    headline: <>Corridor oversight and platform control.<br /><span>Complete audit trail.</span></>,
    text: 'Administrative portal: approve buyer and seller registrations, review web discovery leads, govern catalogue products, and oversee trade intelligence.',
    points: [
      [ShieldCheck, 'Account approvals and complete audit trail'],
      [Building2, 'Web discovery queue with human verification'],
      [Hexagon, 'Plant decision engine and country-pair insights'],
    ],
  },
};

function AuthLayout({ portal, children }: { portal: Portal; children: ReactNode }) {
  const { engineAllowed } = useAuth();
  const normalizedKey = portal === 'admin' || portal === 'super_admin' ? 'staff' : portal;
  const p = PORTALS[normalizedKey] || PORTALS.staff;
  const Points = p.points;
  const isStaffWithoutEngine = normalizedKey === 'staff' && !engineAllowed;
  const headline = isStaffWithoutEngine ? <>Lithium trade intelligence.<br /><span>Direct counterparty matching.</span></> : p.headline;
  const text = isStaffWithoutEngine ? 'Discover buyers and sellers on the web, review every lead before it is published, and approve accounts and products.' : p.text;
  return (
    <main className={`auth-page ${p.cls}`}>
      <section className="auth-story">
        <Link to="/welcome" className="brand">
          <span className="brand-mark"><Hexagon size={22} aria-hidden /></span>
          <span><span className="brand-name">Corridor Intelligence</span><span className="brand-caption">{engineAllowed ? 'LITHIUM TRADE & PLANT DECISIONS' : 'LITHIUM TRADE PORTAL'}</span></span>
        </Link>
        <div className="auth-story-body">
          <p className="eyebrow">{p.eyebrow}</p>
          <h1>{headline}</h1>
          <p>{text}</p>
          <div className="auth-points">{Points.map(([Icon, ptText]) => <span key={ptText}><Icon size={18} aria-hidden />{ptText}</span>)}</div>
        </div>
        <p className="auth-foot">Every company profile shows where its details come from, and every contact links to the page that published it.</p>
      </section>
      <section className="auth-form-side">
        {children}
        <div className="auth-legal tiny">
          <Copyright />
          <ContactLink />
          <LegalLinks />
        </div>
      </section>
    </main>
  );
}

// The questions the platform answers. Each card: the question in the user's words, then what they get to answer it.
const LAYERS = [
  { key: 'engine', icon: Factory, title: 'Plant Decision Engine', question: 'Where should we build a processing plant — and will it make money?',
    answer: 'Cost, margin and risk for each site, with every assumption shown.' },
  { key: 'offtake', icon: Target, title: 'Offtake Intelligence', question: 'Which suppliers can meet our volume, grade, timing and price?',
    answer: 'Sellers and live offers for each material, matched to your requirement.' },
  { key: 'counterparty', icon: ShieldCheck, title: 'Counterparty Checks', question: 'Is this company real, registered and the right one to talk to?',
    answer: 'Company registrations, products, offices and named contact desks in one profile.' },
  { key: 'compliance', icon: FileCheck2, title: 'Trade Compliance', question: 'Is this shipment document-ready for customs and origin rules?',
    answer: 'Documents, duties and trade agreements for each country pair.' },
] as const;
const COUNT_WORD = ['', 'one', 'two', 'three', 'four', 'five'];

const MATERIALS: { code: string; name: string; role: string }[] = [
  { code: 'spodumene_sc6', name: 'Spodumene SC6', role: 'Lithium raw material' },
  { code: 'lioh_battery', name: 'Lithium Hydroxide', role: 'Battery-grade chemical' },
  { code: 'li2co3_battery', name: 'Lithium Carbonate', role: 'Battery-grade chemical' },
  { code: 'nickel_sulphate', name: 'Nickel Sulphate', role: 'Cathode material' },
  { code: 'cobalt_sulphate', name: 'Cobalt Sulphate', role: 'Cathode material' },
  { code: 'graphite_flake', name: 'Graphite Flake', role: 'Anode raw material' },
];

const FLOWS = [
  {
    id: 'buyer', icon: ShoppingCart, title: 'For buyers', cta: 'Register as buyer',
    steps: [
      ['Register your company', 'Company name, country, GSTIN (or your country’s company number) and work email — free.'],
      ['Get approved', 'Our team checks the company before your account goes live.'],
      ['Post a requirement', 'Product, grade, quantity, receiving port, Incoterm and target price.'],
      ['See matched sellers', 'Listings that fit your spec, plus who is selling in each country.'],
      ['Send enquiries', 'Message sellers, compare specs, documents and price.'],
      ['Check and close', 'Use corridor insights for duties, agreements and documents; sign the contract directly.'],
    ],
  },
  {
    id: 'seller', icon: Factory, title: 'For sellers', cta: 'Register as seller',
    steps: [
      ['Register your company', 'Company name, country, GSTIN (or your country’s company number) and work email — free.'],
      ['Get approved', 'Our team checks the company before your account goes live.'],
      ['Complete your profile', 'Dispatch ports, contact desks, certificates and the products you supply.'],
      ['Publish listings', 'Grade, specs, available quantity, indicative price and documents.'],
      ['Reach matched buyers', 'Open requirements that fit what you sell, plus who is buying.'],
      ['Answer and close', 'Reply to enquiries, share documents and agree terms directly.'],
    ],
  },
] as const;

function Photo({ photo, alt, className }: { photo: { url: string; credit: string; source: string }; alt: string; className?: string }) {
  return (
    <figure className={`wl-photo ${className ?? ''}`}>
      <img src={photo.url} alt={alt} loading="lazy" />
      <figcaption><a href={photo.source} target="_blank" rel="noopener noreferrer">Photo: {photo.credit}</a></figcaption>
    </figure>
  );
}

export function WelcomePage() {
  const { user, activeModule, setActiveModule, logout, engineEnabled } = useAuth();
  const navigate = useNavigate();
  const enter = (mode: 'buyer' | 'seller' | 'admin') => { setActiveModule(mode); navigate('/trade'); };
  const defaultMode = activeModule || (user?.role === 'buyer' ? 'buyer' : user?.role === 'seller' ? 'seller' : 'admin');
  const go = (path: string) => navigate(user ? path : `/login/${path === '/' ? 'staff' : 'buyer'}`, { state: { from: path } });
  const layers = LAYERS.filter((l) => l.key !== 'engine' || engineEnabled);
  const layerCols = layers.length === 4 ? 'four' : 'three';

  return (
    <div className="wl-page">
      <header className="wl-nav">
        <div className="wl-nav-inner">
          <Link to="/welcome" className="wl-brand">
            <span className="wl-brand-mark"><Hexagon size={20} aria-hidden /></span>
            <span><span className="wl-brand-name">Corridor Intelligence</span><span className="wl-brand-tag">BATTERY MATERIALS TRADE</span></span>
          </Link>
          <div className="wl-nav-right">
            {user ? (
              <>
                <span className="wl-user"><span className="wl-avatar">{(user.full_name || 'U').charAt(0).toUpperCase()}</span>
                  <span className="wl-user-text"><strong>{user.full_name}</strong><span>{ROLE_LABEL[user.role]}</span></span></span>
                <button type="button" className="button primary sm" onClick={() => enter(defaultMode)}>Open workspace <ArrowRight size={13} aria-hidden /></button>
                <button type="button" className="icon-button" title="Sign out" aria-label="Sign out" onClick={() => void logout()}><LogOut size={16} aria-hidden /></button>
              </>
            ) : (
              <>
                <Link to="/login/buyer" className="button ghost sm">Sign in</Link>
                <Link to="/register/buyer" className="button primary sm">Get started</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="wl-hero">
        <div className="wl-hero-text">
          <span className="wl-pill"><Globe2 size={13} aria-hidden /> Australia → India critical-minerals corridor</span>
          <h1>Source and sell battery materials <span>with confidence.</span></h1>
          <p>Approved counterparties, live market signals and shipment-ready documents — from mine to cell, in one place.</p>
          <div className="wl-cta">
            {user ? (
              <button type="button" className="button primary" onClick={() => enter(defaultMode)}>Open my workspace <ArrowRight size={15} aria-hidden /></button>
            ) : (
              <>
                <Link to="/login/buyer" className="button primary"><ShoppingCart size={15} aria-hidden /> I'm a buyer</Link>
                <Link to="/login/seller" className="button primary seller"><Factory size={15} aria-hidden /> I'm a seller</Link>
              </>
            )}
          </div>
        </div>
        <div className="wl-hero-media">
          <Photo photo={HERO_PHOTOS.port} alt="Bulk carriers loading at Port Hedland, Western Australia" className="main" />
          <Photo photo={HERO_PHOTOS.mine} alt="Greenbushes lithium mine, Western Australia" className="inset" />
        </div>
      </section>

      <section id="materials" className="wl-section">
        <div className="wl-head"><h2>Priority battery materials</h2></div>
        <div className="wl-materials">
          {MATERIALS.map((m) => (
            <div key={m.code} className="wl-material">
              <Photo photo={PRODUCT_PHOTOS[m.code]} alt={m.name} />
              <div><strong>{m.name}</strong><span>{m.role}</span></div>
            </div>
          ))}
        </div>
        <p className="wl-more">Plus manganese, cathode and anode materials, cells and storage packs.</p>
      </section>

      <section id="how-it-works" className="wl-section">
        <div className="wl-head"><h2>How it works</h2><p>From registration to a signed deal, in six steps.</p></div>
        <div className="wl-flows">
          {FLOWS.map((f) => {
            const Icon = f.icon;
            return (
              <article key={f.id} className={`wl-flow wl-flow-${f.id}`}>
                <header><span className="wl-layer-icon"><Icon size={20} aria-hidden /></span><h3>{f.title}</h3></header>
                <ol>
                  {f.steps.map(([title, text]) => <li key={title}><strong>{title}</strong><span>{text}</span></li>)}
                </ol>
                {!user && <Link to={`/register/${f.id}`} className="button primary sm">{f.cta} <ArrowRight size={13} aria-hidden /></Link>}
              </article>
            );
          })}
        </div>
        <p className="wl-flow-note"><ShieldCheck size={15} aria-hidden /> Every account is reviewed by our team before it goes live. Contracts, inspection, shipping and payment are agreed directly between buyer and seller.</p>
      </section>

      <section id="layers" className="wl-section wl-tint">
        <div className="wl-head">
          <h2>One platform, {COUNT_WORD[layers.length]} decisions</h2>
          <p>The questions a battery-materials buyer or seller has to answer before a deal — and where the platform answers them.</p>
        </div>
        <div className={`wl-layers ${layerCols}`}>
          {layers.map((l) => {
            const Icon = l.icon;
            return (
              <article key={l.key} className={`wl-layer wl-layer-${l.key}`}>
                <span className="wl-layer-icon"><Icon size={20} aria-hidden /></span>
                <h3>{l.title}</h3>
                <p className="wl-layer-q">“{l.question}”</p>
                <p className="wl-layer-a"><Check size={14} aria-hidden />{l.answer}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="wl-section">
        <div className="wl-band">
          <Photo photo={HERO_PHOTOS.bess} alt="Battery energy storage system" />
          <div>
            <h2>See any trade corridor at a glance</h2>
            <ul>
              <li><TrendingUp size={16} aria-hidden /> Official import and export values</li>
              <li><Newspaper size={16} aria-hidden /> Latest policy and tariff news</li>
              <li><Landmark size={16} aria-hidden /> Trade agreements that apply</li>
              <li><FileCheck2 size={16} aria-hidden /> Documents each shipment needs</li>
            </ul>
            <button type="button" className="button primary" onClick={() => go('/trade/corridor')}>Explore country pairs <ArrowRight size={15} aria-hidden /></button>
          </div>
        </div>
      </section>

      <section className="wl-section wl-tint">
        <div className="wl-portals">
          {([
            { id: 'buyer', icon: ShoppingCart, title: 'For buyers', text: 'Post requirements and compare matched offers on spec, documents and price.' },
            { id: 'seller', icon: Factory, title: 'For sellers', text: 'List your grades and reach buyers with open requirements.' },
          ] as const).map((p) => {
            const Icon = p.icon;
            const access = user && (user.role === 'super_admin' || user.role === p.id);
            return (
              <article key={p.id} className={`wl-portal wl-portal-${p.id}`}>
                <span className="wl-layer-icon"><Icon size={20} aria-hidden /></span>
                <div><h3>{p.title}</h3><p>{p.text}</p></div>
                {user ? (access && <button type="button" className="button primary sm" onClick={() => enter(p.id)}>Open <ArrowRight size={13} aria-hidden /></button>)
                  : <Link to={`/register/${p.id}`} className="button primary sm">Register free</Link>}
              </article>
            );
          })}
        </div>
      </section>

      <footer className="wl-footer">
        <div className="wl-footer-inner wl-footer-cols">
          <div className="wl-footer-about">
            <span className="wl-brand-name">{SITE.product}</span>
            <p>A B2B platform connecting buyers and sellers of battery materials and critical minerals across the Australia–India corridor and beyond.</p>
            <ContactLink />
          </div>
          <nav aria-label="Platform">
            <strong>Platform</strong>
            <Link to="/register/buyer">Register as buyer</Link>
            <Link to="/register/seller">Register as seller</Link>
            <Link to="/login/buyer">Buyer sign in</Link>
            <Link to="/login/seller">Seller sign in</Link>
            <Link to="/login/admin"><LockKeyhole size={12} aria-hidden /> Staff</Link>
          </nav>
          <nav aria-label="Legal">
            <strong>Legal</strong>
            {LEGAL_DOCS.map((d) => <Link key={d.id} to={`/legal/${d.id}`}>{d.title}</Link>)}
            <button type="button" className="link-button" onClick={openCookieSettings}>Cookie settings</button>
          </nav>
        </div>
        <div className="wl-footer-inner wl-footer-bottom">
          <Copyright />
          <span className="tiny muted">Photos: Wikimedia Commons contributors, credited on each image.</span>
        </div>
      </footer>
    </div>
  );
}

export function LoginPage() {
  const params = useParams();
  const rawParam = params.portal ?? '';
  const portal: Portal = (rawParam === 'seller' ? 'seller' : rawParam === 'buyer' ? 'buyer' : 'staff');
  const pMeta = PORTALS[portal] || PORTALS.staff;

  const { user, login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const from = (location.state as { from?: string } | null)?.from;
  if (user) return <Navigate to={from?.startsWith('/') && !from.startsWith('//') ? from : homeFor(user)} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const me = await login(email.trim(), password, portal);
      navigate(from?.startsWith('/') && !from.startsWith('//') ? from : homeFor(me), { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setPassword('');
      setPending(false);
    }
  }

  return (
    <AuthLayout portal={portal}>
      <div className="auth-form-wrap">
        <div className="login-home-bar">
          <Link to="/welcome" className="button secondary sm" style={{ gap: 6 }}>
            <Home size={14} aria-hidden /> Portals Home
          </Link>
          <span className="tiny muted">{pMeta.eyebrow}</span>
        </div>

        <div className="auth-lock"><LockKeyhole size={20} aria-hidden /></div>
        <h2>{pMeta.title}</h2>
        <p className="tiny muted" style={{ marginBottom: 16 }}>
          Sign in to access your dedicated {portal === 'staff' ? 'Super Admin' : portal} platform.
        </p>

        <form className="form-stack" onSubmit={(e) => void submit(e)}>
          <ErrorNotice error={error} />
          <Field label="Email address">
            <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
          </Field>
          <Field label="Password">
            <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </Field>
          <button type="submit" className="button primary full" disabled={pending}>
            {pending ? 'Signing in…' : `Sign in to ${portal === 'staff' ? 'Admin' : portal} platform`}
            <ArrowRight size={16} aria-hidden />
          </button>
        </form>

        {portal !== 'staff' ? (
          <aside className="setup-note">
            <strong>New here?</strong>
            <p><Link to={`/register/${portal}`}>Register your company as a {portal}</Link>. An administrator approves new accounts.</p>
          </aside>
        ) : status && !status.has_super_admin ? (
          <Notice tone="warn">No administrator account has been set up yet. Please contact your system administrator.</Notice>
        ) : (
          <aside className="setup-note">
            <strong>Super Admin & Staff access</strong>
            <p>Super admin and staff accounts have administrative privileges across platforms. Buyers and sellers sign in via their dedicated portals.</p>
          </aside>
        )}

        <nav className="auth-switch tiny" aria-label="Other sign-in pages">
          {portal !== 'buyer' && <Link to="/login/buyer">Buyer Portal</Link>}
          {portal !== 'seller' && <Link to="/login/seller">Seller Portal</Link>}
          {portal !== 'staff' && <Link to="/login/admin">Super Admin Portal</Link>}
          <Link to="/welcome">← All Portals Home</Link>
        </nav>
      </div>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const params = useParams();
  const role = (params.role === 'seller' ? 'seller' : 'buyer') as 'buyer' | 'seller';
  const { user } = useAuth();
  const [form, setForm] = useState({ email: '', password: '', confirm: '', full_name: '', phone: '', name: '', country: role === 'seller' ? 'IN' : '', city: '', website: '', business_type: '', accept: false });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const [ids, setIds] = useState({ primary: '', iec: '', trades: false });
  const schemes = useSchemes();
  if (user) return <Navigate to={homeFor(user)} replace />;
  const set = (k: keyof typeof form) => (v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (form.password !== form.confirm) { setError(new Error('The two passwords do not match.')); return; }
    setPending(true);
    try {
      const res = await auth.register(role, {
        email: form.email.trim(), password: form.password, full_name: form.full_name, phone: form.phone, accept_terms: form.accept,
        organization: { name: form.name, country: form.country, city: form.city, website: form.website, business_type: form.business_type },
        identifiers: registrationIdentifiers(schemes.data?.primary_by_country[form.country] ?? 'company_reg', ids),
        trades_internationally: form.country === 'IN' && ids.trades,
      });
      setDone(res.message);
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout portal={role}>
      <div className="auth-form-wrap wide">
        <div className="login-home-bar">
          <Link to="/welcome" className="button secondary sm" style={{ gap: 6 }}>
            <Home size={14} aria-hidden /> Portals Home
          </Link>
          <span className="tiny muted">REGISTRATION</span>
        </div>
        <p className="eyebrow">{PORTALS[role].eyebrow}</p>
        <h2>Register as a {role}</h2>
        {done ? (
          <>
            <Notice tone="success">{done}</Notice>
            <Link className="button primary" to={`/login/${role}`}>Go to {role} sign-in</Link>
          </>
        ) : (
          <form className="form-stack" onSubmit={(e) => void submit(e)}>
            <ErrorNotice error={error} />
            <p className="section-label">Your company</p>
            <Field label="Company name"><input type="text" required minLength={2} maxLength={200} value={form.name} onChange={(e) => set('name')(e.target.value)} /></Field>
            <div className="form-grid two">
              <Field label="Country"><CountrySelect value={form.country} onChange={set('country')} required /></Field>
              <Field label="City"><input type="text" maxLength={80} value={form.city} onChange={(e) => set('city')(e.target.value)} /></Field>
            </div>
            <div className="form-grid two">
              <Field label="Website" hint="Optional. Helps buyers and sellers recognise you."><input type="text" placeholder="www.example.com" value={form.website} onChange={(e) => set('website')(e.target.value)} /></Field>
              <Field label="Business type" hint={role === 'seller' ? 'e.g. producer, trader, distributor' : 'e.g. cathode maker, refinery'}>
                <input type="text" maxLength={120} value={form.business_type} onChange={(e) => set('business_type')(e.target.value)} />
              </Field>
            </div>
            <RegistrationIdFields country={form.country} value={ids} onChange={setIds} />
            <p className="section-label">You</p>
            <div className="form-grid two">
              <Field label="Full name"><input type="text" required minLength={2} autoComplete="name" value={form.full_name} onChange={(e) => set('full_name')(e.target.value)} /></Field>
              <Field label="Phone" hint="Optional"><input type="text" autoComplete="tel" value={form.phone} onChange={(e) => set('phone')(e.target.value)} /></Field>
            </div>
            <Field label="Work email"><input type="email" required autoComplete="email" value={form.email} onChange={(e) => set('email')(e.target.value)} /></Field>
            <div className="form-grid two">
              <Field label="Password" hint="At least 8 characters; not your email name."><input type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={(e) => set('password')(e.target.value)} /></Field>
              <Field label="Repeat password"><input type="password" required minLength={8} autoComplete="new-password" value={form.confirm} onChange={(e) => set('confirm')(e.target.value)} /></Field>
            </div>
            <label className="checkbox"><input type="checkbox" checked={form.accept} onChange={(e) => set('accept')(e.target.checked)} required />
              <span>I confirm the company details are accurate, agree that our business contacts are shown to signed-in {role === 'buyer' ? 'sellers' : 'buyers'}, and accept the{' '}
                <Link to="/legal/terms" target="_blank">Terms &amp; Conditions</Link>, <Link to="/legal/privacy" target="_blank">Privacy Policy</Link> and <Link to="/legal/data-policy" target="_blank">Data Policy</Link>.</span></label>
            <button type="submit" className="button primary full" disabled={pending}>{pending ? 'Sending…' : 'Register'}<ArrowRight size={16} aria-hidden /></button>
            <p className="tiny muted">Already registered? <Link to={`/login/${role}`}>Sign in</Link></p>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
